import PostBox from 'components/icons/PostBox';
import Posts from 'components/icons/Posts';
import SetUp from 'components/icons/SetUp';
import roleType from 'constants/roleType';
import Account from '../icons/Account';
import Division from '../icons/Division';
import UserGroup from '../icons/UserGroup';
import Whistleblower from '../icons/Whistleblower';
import Logout from 'components/icons/Logout';
import Sync from 'components/icons/Sync';
import BulletinBoard from '../icons/BulletinBoard';
import Enquetes from '../icons/Enquetes';
import Usage from 'constants/usage';

// Every flag is named Disable*, so the not-loaded-yet `false` reads as "feature on" and the
// menu briefly lists items the tenant has switched off. Feed this in until the usages land:
// a flag-driven item then stays out of the menu instead of appearing and vanishing.
export const ALL_USAGES_DISABLED = Object.keys(Usage).reduce((flags, key) => {
    flags[key] = true;
    return flags;
}, {});

const ADMIN_SIDEBAR_DATA = [
    {
        label: '投稿',
        Icon: Posts,
        url: '/posts',
        id: 'posts',
        permission: [roleType.ROOT, roleType.DEFAULT],
        isEnabled: ({ clientUsages, user }) => {
            if (!clientUsages?.DisablePosting) {
                return true;
            }

            if (user?.has_thread_types?.enquete || user?.has_thread_types?.external) {
                return true;
            }

            return false;
        },
    },
    {
        label: '内部通報',
        Icon: Whistleblower,
        url: '/reports',
        id: 'reports',
        permission: [roleType.INTERNAL_REPORT],
        isEnabled: ({ clientUsages }) => !clientUsages?.DisableInternalReport,
    },
    {
        label: '掲示板',
        Icon: BulletinBoard,
        id: 'bulletin-boards',
        url: '/bulletin-boards',
        notForRole: [roleType.ROOT, roleType.SUPPORT],
        isEnabled: ({ clientUsages }) => !clientUsages?.DisableBulletinBoard,
    },
    {
        label: '掲示板',
        Icon: BulletinBoard,
        id: 'bulletin-boards',
        isEnabled: ({ clientUsages }) => !clientUsages?.DisableBulletinBoard,
        permission: [roleType.ROOT, roleType.SUPPORT],
        children: [
            {
                label: '掲示板一覧',
                url: '/bulletin-boards',
                id: 'bulletin-boards',
            },
            {
                label: '禁止ワード設定',
                url: '/forbidden-words',
                id: 'forbidden-words',
            },
        ],
    },
    {
        label: '投稿設定',
        Icon: Division,
        id: 'post-setting',
        children: [
            {
                label: 'カテゴリ',
                url: '/categories',
                id: 'categories',
                permission: [roleType.ROOT, roleType.SUPPORT],
                isEnabled: ({ clientUsages }) => !clientUsages?.DisablePosting,
            },
            {
                label: 'グループ',
                url: '/groups',
                id: 'groups',
                permission: [roleType.ROOT, roleType.SUPPORT],
                isEnabled: ({ clientUsages }) => !clientUsages?.DisableUserGroup,
            },
            {
                label: '権限',
                url: '/roles',
                id: 'roles',
                permission: [roleType.ROOT, roleType.SUPPORT],
                isEnabled: ({ clientUsages }) =>
                    !clientUsages?.DisableRoleManagement && !clientUsages?.DisablePosting,
            },
        ],
    },
    {
        label: 'メンバー設定',
        Icon: UserGroup,
        id: 'user-setting',
        children: [
            {
                label: 'ユーザー',
                url: '/users',
                id: 'users',
                permission: [roleType.ROOT, roleType.SUPPORT],
            },
            {
                label: '管理者',
                url: '/operators',
                id: 'operators',
                permission: [roleType.ROOT, roleType.SUPPORT],
            },
        ],
    },
    {
        label: 'アンケート',
        Icon: Enquetes,
        url: '/enquetes',
        id: 'enquetes',
        permission: [roleType.ROOT, roleType.DEFAULT, roleType.SUPPORT],
        isEnabled: ({ clientUsages }) => !clientUsages?.DisableEnquete,
    },
    {
        label: 'その他',
        Icon: SetUp,
        id: 'settings',
        children: [
            {
                label: '外部投稿',
                url: '/external-posts',
                id: 'external-posts',
                permission: [roleType.ROOT, roleType.SUPPORT],
                isEnabled: ({ clientUsages }) => !clientUsages?.DisableExternalPost,
            },
            {
                label: 'アナウンス',
                url: '/announcements',
                id: 'announcements',
                permission: [roleType.ROOT, roleType.SUPPORT],
                isEnabled: ({ clientUsages }) => !clientUsages?.DisableAnouncementFunction,
            },
            {
                label: '運用方針',
                url: '/contents',
                id: 'contents',
                permission: [roleType.ROOT, roleType.SUPPORT],
            },
            {
                label: 'テンプレート',
                url: '/templates',
                id: 'templates',
                permission: [roleType.ROOT, roleType.SUPPORT],
            },
            {
                label: 'セキュリティポリシー',
                url: '/password-rules',
                id: 'password-rules',
                permission: [roleType.ROOT, roleType.SUPPORT],
                isEnabled: ({ clientUsages }) => !clientUsages?.DisableSecurityPolicy,
            },
            {
                label: 'IPアドレス制限',
                url: '/access_restrictions',
                id: 'access_restrictions',
                permission: [roleType.ROOT, roleType.SUPPORT],
                isEnabled: ({ clientUsages }) => !clientUsages?.DisableIpRestriction,
            },
        ],
    },
    { label: 'アカウント設定', Icon: Account, url: '/profile', id: 'profile' },
];

const USER_SIDEBAR_DATA = [
    {
        label: '投稿',
        Icon: Posts,
        url: '/posts',
        id: 'posts',
        isEnabled: ({ clientUsages, user }) => {
            if (!clientUsages?.DisablePosting) {
                return true;
            }

            if (user?.has_enquete_threads === true) {
                return true;
            }

            return false;
        },
    },
    {
        label: '内部通報',
        Icon: Whistleblower,
        url: '/reports',
        id: 'reports',
        isEnabled: ({ clientUsages }) => !clientUsages?.DisableInternalReport,
    },
    {
        label: '掲示板',
        Icon: BulletinBoard,
        id: 'bulletin-boards',
        url: '/bulletin-boards',
        isEnabled: ({ clientUsages, user }) => {
            // If bulletin board feature is disabled, hide menu
            if (clientUsages?.DisableBulletinBoard) {
                return false;
            }
            // If feature is enabled, only show if user has access to active bulletin boards
            return user?.has_active_bulletin_boards === true;
        },
    },
    {
        label: 'アンケート',
        Icon: Enquetes,
        url: '/enquetes',
        id: 'enquetes',
        isEnabled: ({ clientUsages, user }) => {
            // If enquete feature is disabled, hide menu
            if (clientUsages?.DisableEnquete) {
                return false;
            }
            // If feature is enabled, only show when the user has assigned surveys
            return user?.has_assigned_enquetes === true;
        },
    },
    { label: 'アカウント設定', Icon: Account, url: '/profile', id: 'profile' },
];

const SYSTEM_SIDEBAR_DATA = [{ label: '企業一覧', Icon: PostBox, url: '/clients', id: 'clients' }];

const SIDEBAR_DATA = {
    admin: ADMIN_SIDEBAR_DATA,
    user: USER_SIDEBAR_DATA,
    system: SYSTEM_SIDEBAR_DATA,
};

export const BOTTOM_SIDEBAR_DATA = [
    {
        label: '運用方針',
        Icon: SetUp,
        url: '/contents',
        id: 'contents',
        screen: 'sp',
        isEnabled: ({ user }) => !user?.roles, // is User: user does not have roles
    },
    {
        label: '管理者切替',
        Icon: Sync,
        url: '/switch',
        id: 'switch',
        screen: 'sp',
        isEnabled: ({ user }) => !!user?.is_linked,
    },
    { label: 'ログアウト', Icon: Logout, url: '/logout', id: 'logout' },
];

const notForRole = roleTypes => item => {
    if (!roleTypes) return true;

    if (!item.notForRole) return true;

    return item.notForRole.every(type => !roleTypes.includes(type));
};

const filterByPermission = roleTypes => item => {
    if (!roleTypes) return true;

    const hasPermissionAttr = item.permission !== undefined;
    return hasPermissionAttr ? roleTypes?.some(type => item.permission.includes(type)) : true;
};

const filterByScreen = screen => item => {
    const hasScreenAttr = item.screen !== undefined;
    return hasScreenAttr ? item.screen === screen : true;
};

const filterByIsEnabled = ({ clientUsages, user, screen }) => item => {
    const hasIsEnabledAttr = item.isEnabled !== undefined;
    return hasIsEnabledAttr ? item.isEnabled({ clientUsages, user, screen }) : true;
};

/**
 * Build Menu (Filter & Mapping)
 *
 * @param {array} items
 * @param {{clientUsages, user, screen}} options
 * @returns Array
 */
export const buildMenu = (items, { clientUsages, user, screen }) => {
    const roleTypes = user?.roles?.map(role => role.type);

    return (
        items
            // filters
            .filter(filterByPermission(roleTypes))
            .filter(filterByScreen(screen))
            .filter(filterByIsEnabled({ clientUsages, user, screen }))
            .filter(notForRole(roleTypes))
            // map (filter children recursively)
            .map(item => ({
                ...item,
                children:
                    item.children?.length > 0
                        ? buildMenu(item.children, { clientUsages, user, screen })
                        : undefined,
            }))
            // filter out empty children
            .filter(item => {
                const hasUrl = !!item.url;
                const hasChildren = item.children?.length > 0;
                return hasUrl || hasChildren;
            })
    );
};

/**
 * Get sidebar data by user type & client usages
 * @param {string} userType
 * @param {User} userData
 * @param {object} clientUsages
 * @param {string} screen ('pc'|'sp')
 * @returns array
 */
export const sidebarData = (userType, userData, clientUsages, screen = 'pc') => {
    const menuList = SIDEBAR_DATA[userType];

    return buildMenu(menuList, { clientUsages, user: userData, screen });
};

/**
 * Find first enabled URL from sidebar menu items
 * Checks items in order and returns the first URL that passes all filters
 * @param {array} items - Sidebar menu items
 * @param {{clientUsages, user, screen}} options - Filter options
 * @returns {string|null} - First enabled URL or null if none found
 */
const findFirstEnabledUrl = (items, { clientUsages, user, screen }) => {
    const roleTypes = user?.roles?.map(role => role.type);

    for (const item of items) {
        const hasPermission = filterByPermission(roleTypes)(item);
        const hasScreen = filterByScreen(screen)(item);
        const hasIsEnabled = filterByIsEnabled({ clientUsages, user, screen })(item);
        const hasNotForRole = notForRole(roleTypes)(item);

        if (hasPermission && hasScreen && hasIsEnabled && hasNotForRole) {
            if (item.url) {
                return item.url;
            }

            if (item.children?.length > 0) {
                const childUrl = findFirstEnabledUrl(item.children, { clientUsages, user, screen });
                if (childUrl) {
                    return childUrl;
                }
            }
        }
    }

    return null;
};

/**
 * Get first enabled page URL for default redirect
 * @param {string} userType - 'admin' | 'user' | 'system'
 * @param {User} userData - User data object
 * @param {object} clientUsages - Client usage flags
 * @param {string} screen - 'pc' | 'sp' (default: 'pc')
 * @returns {string|null} - First enabled URL or null if none found
 */
export const getFirstEnabledUrl = (userType, userData, clientUsages, screen = 'pc') => {
    const menuList = SIDEBAR_DATA[userType];
    if (!menuList) {
        return null;
    }

    return findFirstEnabledUrl(menuList, { clientUsages, user: userData, screen });
};
